import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  ACCOUNT_USERS_SEED,
  GUESTS_SEED,
  QUESTIONNAIRES_SEED,
  REVIEWS_LISTING_SEED,
  SERVICE_TICKETS_SEED,
} from '@common/data';
import { GLOBAL_SEARCH_FILTERS, GlobalSearchFilterId, USER_SETTINGS_NAV } from '@common/enums';
import type {
  GlobalSearchRequest,
  GlobalSearchResponse,
  GlobalSearchResult,
  GlobalSearchResultGroup,
} from '@common/types';

import { AdminAccountService } from './admin-account.service';
import { ReportsService } from './reports.service';

const SEARCH_LATENCY_MS = 220;
const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS_PER_GROUP = 5;

// Seed statuses and report types are kebab/lower keys; results show them as labels.
function titleCase(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function matches(term: string, ...fields: readonly (string | null | undefined)[]): boolean {
  return fields.some((field) => !!field && field.toLowerCase().includes(term));
}

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly admin = inject(AdminAccountService);
  private readonly reports = inject(ReportsService);

  search(request: GlobalSearchRequest): Observable<GlobalSearchResponse> {
    const term = request.query.trim().toLowerCase();

    if (term.length < MIN_QUERY_LENGTH) {
      return of({ query: request.query, groups: [], totalCount: 0 });
    }

    // An empty filter list means "every source" — the panel's default state.
    const requested = new Set(request.filters);
    const groups = GLOBAL_SEARCH_FILTERS.filter(
      (filter) => requested.size === 0 || requested.has(filter.id),
    )
      .map((filter) => {
        const results = this.resultsFor(filter.id, term);
        return {
          filterId: filter.id,
          label: filter.label,
          icon: filter.icon,
          results: results.slice(0, MAX_RESULTS_PER_GROUP),
          totalCount: results.length,
        } satisfies GlobalSearchResultGroup;
      })
      .filter((group) => group.totalCount > 0);

    const response: GlobalSearchResponse = {
      query: request.query,
      groups,
      totalCount: groups.reduce((sum, group) => sum + group.totalCount, 0),
    };

    return of(response).pipe(delay(SEARCH_LATENCY_MS));
  }

  private resultsFor(filterId: GlobalSearchFilterId, term: string): readonly GlobalSearchResult[] {
    switch (filterId) {
      case GlobalSearchFilterId.Reports:
        return this.searchReports(term);
      case GlobalSearchFilterId.Guests:
        return this.searchGuests(term);
      case GlobalSearchFilterId.Reviews:
        return this.searchReviews(term);
      case GlobalSearchFilterId.Questionnaires:
        return this.searchQuestionnaires(term);
      case GlobalSearchFilterId.ServiceTickets:
        return this.searchServiceTickets(term);
      case GlobalSearchFilterId.Users:
        return this.searchUsers(term);
      case GlobalSearchFilterId.Settings:
        return this.searchSettings(term);
    }
  }

  private searchReports(term: string): readonly GlobalSearchResult[] {
    return this.reports
      .allReports()
      .filter((report) => matches(term, report.name, report.description, report.createdBy))
      .map((report) => ({
        id: `report-${report.id}`,
        filterId: GlobalSearchFilterId.Reports,
        title: report.name,
        subtitle: report.description || undefined,
        meta: report.isFavourite ? 'Favourite' : titleCase(report.type),
        icon: report.isFavourite ? 'star' : 'summarize',
        routerLink: ['/reports', report.id, 'view'],
      }));
  }

  private searchGuests(term: string): readonly GlobalSearchResult[] {
    return GUESTS_SEED.filter((guest) => matches(term, guest.name, guest.email, guest.room)).map(
      (guest) => ({
        id: `guest-${guest.id}`,
        filterId: GlobalSearchFilterId.Guests,
        title: guest.name,
        subtitle: guest.email,
        meta: `Room ${guest.room}`,
        icon: 'person',
        routerLink: '/guests/manage',
      }),
    );
  }

  private searchReviews(term: string): readonly GlobalSearchResult[] {
    return REVIEWS_LISTING_SEED.filter((review) =>
      matches(
        term,
        review.displayName,
        review.email,
        review.propertyName,
        review.source.description,
      ),
    ).map((review) => ({
      id: `review-${review.id}`,
      filterId: GlobalSearchFilterId.Reviews,
      title: review.displayName,
      subtitle: `${review.propertyName} · ${review.source.description}`,
      meta: review.reviewRating === null ? undefined : `${review.reviewRating}%`,
      icon: 'rate_review',
      routerLink: '/reviews',
    }));
  }

  private searchQuestionnaires(term: string): readonly GlobalSearchResult[] {
    const accountId = this.admin.accountId();
    return QUESTIONNAIRES_SEED.filter((questionnaire) =>
      matches(term, questionnaire.name, questionnaire.title),
    ).map((questionnaire) => ({
      id: `questionnaire-${questionnaire.id}`,
      filterId: GlobalSearchFilterId.Questionnaires,
      title: questionnaire.name,
      subtitle: questionnaire.title,
      meta: titleCase(questionnaire.status),
      icon: 'fact_check',
      routerLink: accountId
        ? ['/account', accountId, 'questionnaires', questionnaire.id]
        : '/accounts',
    }));
  }

  private searchServiceTickets(term: string): readonly GlobalSearchResult[] {
    return SERVICE_TICKETS_SEED.filter((ticket) =>
      matches(term, ticket.subject, ticket.guestName, ticket.assignee?.name),
    ).map((ticket) => ({
      id: `ticket-${ticket.id}`,
      filterId: GlobalSearchFilterId.ServiceTickets,
      title: ticket.subject,
      subtitle: ticket.assignee
        ? `${ticket.guestName} · ${ticket.assignee.name}`
        : ticket.guestName,
      meta: titleCase(ticket.status),
      icon: 'confirmation_number',
      routerLink: '/service-tickets/manage',
    }));
  }

  /**
   * Account users are seeded without an account of their own, so the destination is the account
   * currently being administered. Settings now require that id in the path - a bare /account
   * link resolves to nothing and bounces the user to the listing.
   */
  private searchUsers(term: string): readonly GlobalSearchResult[] {
    const accountId = this.admin.accountId();
    return ACCOUNT_USERS_SEED.filter((user) =>
      matches(term, `${user.name} ${user.surname}`, user.email, user.title),
    ).map((user) => ({
      id: `user-${user.id}`,
      filterId: GlobalSearchFilterId.Users,
      title: `${user.name} ${user.surname}`.trim() || user.email,
      subtitle: user.email,
      meta: titleCase(user.status),
      icon: 'badge',
      routerLink: accountId ? ['/account', accountId, 'users'] : '/accounts',
    }));
  }

  private searchSettings(term: string): readonly GlobalSearchResult[] {
    return USER_SETTINGS_NAV.flatMap((group) =>
      group.entries
        .filter((entry) => matches(term, entry.label, group.label, ...(entry.keywords ?? [])))
        .map((entry) => ({
          id: `settings-${entry.path}`,
          filterId: GlobalSearchFilterId.Settings,
          title: entry.label,
          subtitle: `My settings · ${group.label}`,
          icon: group.icon,
          routerLink: ['/settings', entry.path],
        })),
    );
  }
}
